import React, { useMemo } from 'react';

import { ConfluenceUpgradeFreePoolExperimentModalPreUpgrade } from '@atlassian/upgrade-free-pool-target-experiment';
import { ErrorType, Severity } from '@atlassian/growth-commons-error-analytics';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';

import { ExperimentErrorBoundaryWrapper } from '../common/ErrorBoundaryWrapper.tsx';
import { useExperimentErrorAnalytics } from '../utils/useExperimentErrorAnalytics.ts';
import { ConfluenceFreePoolTargetExperimentGUPSQuery } from '../pre-upgrade-modal-graphql-query/ConfluenceFreePoolTargetExperimentGUPSQuery.ts';
import type {
	ConfluenceFreePoolTargetExperimentGUPSQuery$data,
	ConfluenceFreePoolTargetExperimentGUPSQuery$variables,
} from '../pre-upgrade-modal-graphql-query/__generated__/ConfluenceFreePoolTargetExperimentGUPSQuery.graphql.ts';

import { ConfluenceFreePoolTargetExperimentAddtlEligibilityQuery } from './ConfluenceFreePoolTargetExperimentAddtlEligibilityQuery.ts';
import type {
	ConfluenceFreePoolTargetExperimentAddtlEligibilityQuery$data,
	ConfluenceFreePoolTargetExperimentAddtlEligibilityQuery$variables,
} from './__generated__/ConfluenceFreePoolTargetExperimentAddtlEligibilityQuery.graphql.ts';
import { useIsEligibleViaGUPSForExperiment } from './useIsEligibleViaGUPSForExperiment.ts';
import { useIsEligibleForExperiment } from './useIsEligibleForExperiment.ts';
import { useUpgradeFreePoolTargetExperimentCohort } from './useUpgradeFreePoolTargetExperimentCohort.ts';

export const UpgradeFreePoolExperimentModalPreUpgradeModalBaseComponent = () => {
	const { cloudId, edition, isLoggedIn, activationId, userId } = useSessionData();
	const { trackError } = useExperimentErrorAnalytics({
		defaultAdditionalAttributes: {
			edition,
		},
		experimentKey: 'twcg-206_confluence_upgrade_permanent_free',
	});

	const isLicensed = useIsLicensed();

	const canSkipQuery =
		!cloudId ||
		!edition ||
		edition !== ConfluenceEdition.FREE ||
		!isLoggedIn ||
		!activationId ||
		!userId ||
		!isLicensed;

	// The GUPS query is SSR'd since it will whittle down the experiment audience significantly
	const { data: gupsEligibilityData } = useQueryInterop<
		ConfluenceFreePoolTargetExperimentGUPSQuery$data,
		ConfluenceFreePoolTargetExperimentGUPSQuery$variables
	>(ConfluenceFreePoolTargetExperimentGUPSQuery, {
		variables: {
			cloudId,
			productKey: 'confluence.ondemand',
		},
		skip: canSkipQuery,
		fetchPolicy: 'cache-first', // for SSR'ing purposes
		client: getAGGClient(),
		onError: (graphqlError) => {
			trackError({
				errorType: ErrorType.GRAPHQL,
				errorMessage: graphqlError.message,
				severity: Severity.HIGH,
				source: 'ConfluenceFreePoolTargetExperimentGUPSQuery',
				cohort: undefined,
				additionalAttributes: {
					variables: {
						cloudId,
						productKey: 'confluence.ondemand',
					},
					canSkipQuery,
					errorStack: graphqlError.stack,
					errorName: graphqlError.name,
				},
			});
		},
	});

	const { isEligibleViaGUPS, analyticsAttributes: gupsAnalyticsAttributes } =
		useIsEligibleViaGUPSForExperiment(gupsEligibilityData);

	// Only request for other eligibility criteria if eligible via GUPS
	const { data } = useQueryInterop<
		ConfluenceFreePoolTargetExperimentAddtlEligibilityQuery$data,
		ConfluenceFreePoolTargetExperimentAddtlEligibilityQuery$variables
	>(ConfluenceFreePoolTargetExperimentAddtlEligibilityQuery, {
		variables: {
			cloudId,
			productKey: 'confluence.ondemand',
		},
		skip: canSkipQuery || !isEligibleViaGUPS,
		fetchPolicy: 'cache-first',
		client: getAGGClient(),
		onError: (graphqlError) => {
			trackError({
				errorType: ErrorType.GRAPHQL,
				errorMessage: graphqlError.message,
				severity: Severity.HIGH,
				source: 'ConfluenceFreePoolTargetExperimentAddtlEligibilityQuery',
				cohort: undefined,
				additionalAttributes: {
					variables: {
						cloudId,
						productKey: 'confluence.ondemand',
					},
					canSkipQuery: canSkipQuery || !isEligibleViaGUPS,
					errorStack: graphqlError.stack,
					errorName: graphqlError.name,
				},
			});
		},
	});

	const {
		isEligible: isEligibleViaAddtlAttributes,
		experienceUrl,
		analyticsAttributes: addtlAnalyticsAttributes,
	} = useIsEligibleForExperiment(data, edition);

	const confluenceStorageAdapter = useMemo(
		() => ({
			get: (key: string) => {
				return confluenceLocalStorageInstance.getItem(key);
			},
			set: (key: string, value: string) => {
				confluenceLocalStorageInstance.setItem(key, value);
			},
			removeItem: (key: string) => {
				confluenceLocalStorageInstance.removeItem(key);
			},
		}),
		[],
	);

	const analyticsAttributes = useMemo(() => {
		return {
			...gupsAnalyticsAttributes,
			...addtlAnalyticsAttributes,
		};
	}, [gupsAnalyticsAttributes, addtlAnalyticsAttributes]);

	const { abCohort } = useUpgradeFreePoolTargetExperimentCohort({
		isEligible: isEligibleViaGUPS && isEligibleViaAddtlAttributes,
		analyticsAttributes,
		confluenceStorageAdapter,
	});

	if (abCohort !== 'variation') {
		return null;
	}

	return (
		<ExperimentErrorBoundaryWrapper
			experimentKey="twcg-206_confluence_upgrade_permanent_free"
			source="UpgradeFreePoolExperimentModalPreUpgradeModalBaseComponent"
			cohort={abCohort}
			additionalAttributes={{
				cloudId,
				edition,
				experienceUrl,
				isEligibleViaGUPS,
				isEligibleViaAddtlAttributes,
				analyticsAttributes,
			}}
		>
			<ConfluenceUpgradeFreePoolExperimentModalPreUpgrade
				storageAdapter={confluenceStorageAdapter}
				experienceUrl={experienceUrl}
				analyticsAttributes={analyticsAttributes}
			/>
		</ExperimentErrorBoundaryWrapper>
	);
};
